import {
  composeClassNames,
  ifDefined,
  withDefault,
} from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import AttachedDocuments from "src/components/AttachedDocuments";
import ContentProperties from "src/components/ContentProperties";
import ContentSection from "src/components/ContentSection";
import Footer from "src/components/Footer";
import Footers from "src/components/Footers";
import Gallery from "src/components/Gallery";
import Header from "src/components/Header";
import Headers from "src/components/Headers";
import Hr from "src/components/Hr";
import InstructorHighlight from "src/components/InstructorHighlight";
import Page from "src/components/Page";
import PayuCheck from "src/components/PayuCheck";
import Preloader from "src/components/Preloader";
import ReadMore from "src/components/ReadMore";
import RichText from "src/components/RichText";
import SerieIntroduction from "src/components/SerieIntroduction";
import ValidateContent from "src/components/ValidateContent";
import VideoList from "src/components/VideoList";
import constants from "src/constants";
import { UserContext } from "src/contexts/UserContext";
import defaultThumbnail from "src/functions/defaultThumbnail";
import getOg from "src/functions/getOg";
import getProductUserData from "src/functions/getProductUserData";
import loadCustom from "src/functions/loadCustom";
import { mapContentToVideoCards } from "src/functions/mapContentToVideoCards";
import translate from "src/functions/translate";
import { sPageSerie } from "src/styles/sPageSerie.module.scss";
import { ApiLectureNested } from "src/types/ApiLectureNested";

export default function Serie({ dSerie }) {
  var { locale: lang } = useRouter();
  const [freeContent, setFreeContent] = useState(null);
  const [payuCheckReady, setPayuCheckReady] = useState(false);
  const { loggedUser, refetch, loading } = useContext(UserContext);

  const { isPaid, inCart, activeTrialSubscription } = getProductUserData(
    loggedUser,
    dSerie.id,
    dSerie.subscription_allowed,
    "program"
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payuCheckReady]);

  var serieTitle = dSerie
    ? dSerie.title
    : translate(constants.Marketing.ogTitle, lang);
  var serieLabel = dSerie
    ? dSerie.label
    : translate(constants.Marketing.ogDescription, lang);
  var lectures = dSerie
    ? dSerie.lectures.filter((lecture: ApiLectureNested) => lecture.paid)
    : [];

  useEffect(() => {
    const fc = dSerie && loadCustom(dSerie?.id, "serie", "freeContent");
    fc && setFreeContent(fc);
  }, [dSerie, dSerie?.id]);

  const ogData = getOg(
    lang,
    "program",
    serieTitle,
    serieLabel,
    withDefault(dSerie ? dSerie.thumbnail : ``, defaultThumbnail()).url
  );

  return (
    <>
      <Headers data={ogData} />

      <Page className={composeClassNames([sPageSerie, "page-serie"])}>
        <Header />
        <PayuCheck setPayuCheckReady={setPayuCheckReady} />
        {dSerie ? (
          <ValidateContent
            element={`Serie`}
            blocked={withDefault(dSerie.blocked, true)}
            verified={ifDefined(
              dSerie.verified,
              (value) => value.verified,
              () => false
            )}
          >
            <ContentSection className={`in-detail`} fluid={`xl`}>
              <SerieIntroduction
                isPaid={isPaid}
                inCart={inCart}
                data={dSerie}
                className={`border-bottom`}
                activeTrialSubscription={activeTrialSubscription}
                loggedUserClientId={String(loggedUser?.id)}
                loading={loading}
              />
              {constants.Show.freeClassesInSeries &&
              freeContent &&
              freeContent?.lectures &&
              freeContent?.lectures?.length > 0 ? (
                <VideoList
                  data={mapContentToVideoCards(lectures).filter((obj) =>
                    freeContent?.lectures?.includes(obj.id)
                  )}
                  description={translate(freeContent?.description, lang)}
                  className="free-classes"
                  size="lg"
                  type="class"
                  title={translate(freeContent?.title, lang)}
                  showPrivate={dSerie.private}
                />
              ) : null}
              {dSerie.description_text?.length > 0 && (
                <div className={"serie-description"}>
                  <ReadMore
                    rowsVisible={constants.Text.descriptionRowsMedium}
                    rowsCount={
                      dSerie.description_text.length /
                      constants.Text.descriptionCharsPerRow
                    }
                  >
                    <RichText size="xn">
                      <ReactMarkdown>{dSerie.description_text}</ReactMarkdown>
                    </RichText>
                  </ReadMore>
                  <Hr variant={`light`} />
                </div>
              )}
              {lectures && lectures.length > 0 && (
                <VideoList
                  data={
                    !(constants.Show.freeClassesInSeries && freeContent)
                      ? mapContentToVideoCards(lectures)
                      : mapContentToVideoCards(lectures).filter(
                          (item) => !freeContent?.lectures?.includes(item.id)
                        )
                  }
                  size="lg"
                  type="class"
                  title={
                    {
                      serie: translate(`Serie classes`, lang),
                      festival: translate(`Festival classes`, lang),
                    }[dSerie.content_type]
                  }
                  showPrivate={dSerie.private}
                />
              )}
              <ContentProperties
                className={`border-top`}
                benefits={dSerie.benefits}
                contraindications={dSerie.contraindications}
                duration={dSerie.duration}
                difficulty={dSerie.difficulty}
                practice_styles={dSerie.practice_styles}
                practice_skills={dSerie.practice_skills}
                practice_body_parts={dSerie.practice_body_parts}
                practice_equipments={dSerie.practice_equipments}
                practice_focuses={dSerie.practice_focuses}
                verified={dSerie.verified}
              />
              <AttachedDocuments
                data={dSerie.document}
                parentPaid={dSerie.paid}
                isPaid={isPaid}
              />
            </ContentSection>
            {/* TODO: more instructors in one lecture are not implemented, implement when is needed */}
            <InstructorHighlight data={dSerie.profiles[0]} />
            <Gallery
              data={dSerie.gallery}
              title={
                {
                  serie: translate(`Serie gallery`, lang),
                  festival: translate(`Festival gallery`, lang),
                }[dSerie.type]
              }
              className={`border-top`}
            />
          </ValidateContent>
        ) : (
          <Preloader />
        )}
        <Footer />
      </Page>
      <Footers />
    </>
  );
}

export const getServerSideProps = async (context) => {
  const slug = context.params.slug;
  const dSerie = await fetch(`${constants.Paths.apiUrl}/programs/${slug}`).then(
    (res) => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error("API Error");
      }
    }
  );
  return {
    props: { dSerie },
  };
};
